/**
 * OrbitPrint — Members: who belongs to this organization, invite by email,
 * change roles, remove. Managers only (the server enforces it).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { useOrbitAuth, useOrbitPath, orbitApi, errText } from '../OrbitAuthContext';
import { usePlan } from '../plan';
import { OrbitWordmark, OrbitButton, OrbitInput, Lamp } from '../components/OrbitShell';
import '../orbit.css';

function ago(iso) { try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ''; } }

export default function OrbitMembersPage() {
  const { user, org, logout } = useOrbitAuth();
  const p = useOrbitPath();
  const manager = org?.role === 'owner' || org?.role === 'admin';
  const [members, setMembers] = useState(null);
  // the plan picture (slice 4): a hard seat cap (Free) fades the invite button with the reason
  const { plan: planInfo, refresh: refreshPlan } = usePlan();
  const seatCap = planInfo?.limits?.members ?? null;
  const seatsUsed = planInfo?.usage?.members ?? (members?.length || 0);
  const atCap = seatCap != null && seatsUsed >= seatCap;
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState(null);

  const load = useCallback(async () => {
    if (!org?.id) return;
    try {
      const [m, i] = await Promise.all([orbitApi.get(`/orgs/${org.id}/members`), orbitApi.get(`/orgs/${org.id}/invites`)]);
      setMembers(m.data); setInvites(i.data);
    } catch (err) { toast.error(errText(err)); setMembers((prev) => prev || []); }
  }, [org?.id]);
  useEffect(() => { load(); }, [load]);

  if (org && !manager) return <Navigate to={p('/')} replace />;

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true); setLastLink(null);
    try {
      const r = await orbitApi.post(`/orgs/${org.id}/invites`, { email, role, origin: window.location.origin });
      toast.success(r.data.message);
      if (r.data.link) setLastLink(r.data.link);
      setEmail(''); load();
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  const setMemberRole = async (m, newRole) => {
    try { await orbitApi.put(`/orgs/${org.id}/members/${m.user_id}`, { role: newRole }); toast.success('Role updated'); load(); }
    catch (err) { toast.error(errText(err)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.full_name || m.email} from ${org.name}? They lose access to every printer here.`)) return;
    try { await orbitApi.delete(`/orgs/${org.id}/members/${m.user_id}`); toast.success('Member removed'); load(); refreshPlan(); }
    catch (err) { toast.error(errText(err)); }
  };
  const revoke = async (inv) => {
    try { await orbitApi.delete(`/orgs/${org.id}/invites/${inv.id}`); toast.success('Invitation revoked'); load(); }
    catch (err) { toast.error(errText(err)); }
  };

  return (
    <div className="orbit-root min-h-screen">
      <header className="orbit-topbar">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <OrbitWordmark small />
          <div className="flex items-center gap-3">
            <Link to={p('/')} className="orbit-link is-muted text-sm">Dashboard</Link>
            <span className="hidden md:inline text-sm orbit-muted">{user?.email}</span>
            <OrbitButton variant="quiet" size="small" className="!h-9 !text-[11px]" onClick={logout}>Sign out</OrbitButton>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-5 sm:px-8 py-9">
        <p className="orbit-label orbit-boot orbit-d1 m-0">{org?.name}</p>
        <h1 className="orbit-display orbit-boot orbit-d2 mt-1" style={{ fontSize: 'clamp(36px, 5vw, 56px)' }}>Members</h1>
        <p className="orbit-boot orbit-d3 orbit-ink2 mt-3 max-w-xl">People you invite can send documents to the printers you share. New members wait for a manager's OK on every printer until you open printers for them from the printer's "Edit sharing".</p>

        <section className="orbit-card is-kit orbit-boot orbit-d4 mt-8 p-6" data-testid="orbit-invite-card">
          <h2 className="orbit-display" style={{ fontSize: 24 }}>Invite someone</h2>
          {seatCap != null && (
            <p className="text-sm mt-2 m-0" style={{ color: atCap ? 'var(--ob-amber)' : 'var(--ob-mute)' }} data-testid="orbit-invite-cap">
              {atCap
                ? <>The {planInfo.plan_label} plan includes {seatCap} members and all {seatCap} seats are taken. <Link to={p('/plans')} className="orbit-link">Upgrade</Link> to invite more.</>
                : <>{seatsUsed} of {seatCap} seats on the {planInfo.plan_label} plan.{planInfo.paused?.members > 0 ? ` ${planInfo.paused.members} paused by plan.` : ''}</>}
            </p>
          )}
          {planInfo?.limits?.extra_member && planInfo.extra_members > 0 && (
            <p className="text-sm mt-2 m-0 orbit-muted" data-testid="orbit-invite-extra-seats">{planInfo.limits.included_members} seats included · {planInfo.extra_members} extra at ₱{planInfo.limits.extra_member} each a month.</p>
          )}
          <form onSubmit={invite} className="mt-4 grid gap-3 sm:grid-cols-[1fr_160px_auto] sm:items-end">
            <OrbitInput label="Email" type="email" name="invite_email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" required testid="orbit-invite-email" />
            <div className="orbit-field mb-4">
              <label className="orbit-label" htmlFor="invite-role">Role</label>
              <select id="invite-role" className="orbit-input" value={role} onChange={(e) => setRole(e.target.value)} data-testid="orbit-invite-role">
                <option value="member">Member — prints</option>
                <option value="admin">Admin — manages too</option>
              </select>
            </div>
            <div className="mb-4" title={atCap ? 'Every seat on this plan is taken' : undefined}><OrbitButton type="submit" size="small" busy={busy} disabled={atCap} testid="orbit-invite-submit">Send invitation</OrbitButton></div>
          </form>
          {lastLink && (
            <div className="orbit-panel p-4 mt-2 text-sm" data-testid="orbit-invite-link">
              <p className="m-0 font-semibold">Email isn't set up on this server, so share this link yourself:</p>
              <p className="orbit-mono text-xs mt-1 m-0 break-all" style={{ color: 'var(--ob-green)' }}>{lastLink}</p>
            </div>
          )}
          {invites.length > 0 && (
            <ul className="list-none p-0 m-0 mt-5">
              {invites.map((inv) => (
                <li key={inv.id} className="orbit-prow" data-testid={`orbit-invite-${inv.id}`}>
                  <div className="flex-1 min-w-0"><div className="font-semibold text-[15px] truncate">{inv.email}</div><div className="orbit-mono text-[11px] orbit-muted">invited {ago(inv.created_at)} · {inv.role} · expires {ago(inv.expires_at)}</div></div>
                  <Lamp state="busy">Pending</Lamp>
                  <button className="orbit-link is-muted text-xs" onClick={() => revoke(inv)}>Revoke</button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="orbit-card orbit-boot orbit-d5 mt-6 p-6" data-testid="orbit-members-card">
          <h2 className="orbit-display" style={{ fontSize: 24 }}>People</h2>
          {members === null ? <p className="orbit-mono text-sm orbit-muted mt-3">Loading…</p> : (
            <ul className="list-none p-0 m-0 mt-2">
              {members.map((m) => (
                <li key={m.user_id} className="orbit-prow" data-testid={`orbit-member-${m.user_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[15px] truncate">{m.full_name || m.email}{m.user_id === user?.id ? <span className="orbit-muted"> · you</span> : null}</div>
                    <div className="orbit-mono text-[11px] orbit-muted truncate">{m.email} · joined {ago(m.joined_at)}{m.paused_by_plan ? ' · paused by plan' : ''}</div>
                  </div>
                  {m.paused_by_plan && <Lamp state="error" title="Beyond the plan's member count — cannot print until the plan grows" testid={`orbit-member-paused-${m.user_id}`}>Paused</Lamp>}
                  {m.role === 'owner' ? <span className="orbit-label" style={{ color: 'var(--ob-green)' }}>owner</span> : (
                    <select className="orbit-input !text-sm !w-auto" value={m.role} disabled={m.user_id === user?.id} onChange={(e) => setMemberRole(m, e.target.value)} data-testid={`orbit-member-role-${m.user_id}`}>
                      <option value="member">member</option><option value="admin">admin</option>
                    </select>
                  )}
                  {m.role !== 'owner' && m.user_id !== user?.id && <button className="orbit-link is-danger text-xs" onClick={() => remove(m)}>Remove</button>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
